import React, { useCallback, useEffect, useState } from 'react';
import { Button, Space, Spin, Tooltip, Typography, theme } from 'antd';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { FileCheck2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCanWrite } from '@/components/ModuleGate';
import PageModal from '@/components/PageModal';
import StatusIndicator, { StatusNotice } from '@/components/genealogy/StatusIndicator';
import ApprovalSignModal, { postAct } from '@/pages/Batch-Processing/Recipe/ApprovalSignModal';

// REX-01 R-E1: the parentless "Certified Record Export" module (create = certify). It only words the
// tooltip; the button opens the server's preflight, which says whether this person may certify, and
// the act itself decides (403 CERTIFY_NOT_PERMITTED) — the client never decides (§6.2).
const EXPORT_MODULES = ['Certified Record Export'];
const MEANING = 'record certified true copy';

const { Text } = Typography;
const bp = '/bp';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');
const sessionUser = () => {
  try { const u = JSON.parse(localStorage.getItem('user_details') || '{}'); return u?.username || u?.user_name || ''; } catch (e) { return ''; }
};

/** Server coverage status → indicator state (the words shown are the server's). */
const COVERAGE_STATE = { OK: 'ok', FAILED: 'error', NOT_COVERED: 'warning' };
/** Server integrity standing → indicator state. */
const INTEGRITY_STATE = { VERIFIED: 'ok', TAMPERED: 'error', UNVERIFIED: 'unknown' };

/** A refusal exactly as the server worded it: code, then message, then any named sections. */
function Refusal({ refusal }) {
  if (!refusal) return null;
  return (
    <StatusNotice state="error" title={refusal.code || 'Refused'}>
      <div>{refusal.message}</div>
      {(refusal.sections || []).map((s) => (
        <div key={s.section}><Text strong>{s.section}</Text> · {s.status} · {s.reason}</div>
      ))}
    </StatusNotice>
  );
}

/** The preflight, read before anyone is asked for a password (REX-01 R-B3 / R-F1). */
function Preflight({ data }) {
  const { token } = theme.useToken();
  if (!data) return null;
  const t = data.template || {};
  const rv = data.review || {};
  // each fact is a labelled cell (small caps label over the value), laid out two to a row
  const cell = (label, body, span2 = false) => (
    <div style={{ gridColumn: span2 ? '1 / -1' : undefined, minWidth: 0, padding: '8px 12px', border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadius, background: token.colorFillAlter }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: token.colorTextSecondary, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13.5, overflowWrap: 'anywhere' }}>{body}</div>
    </div>
  );
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        {cell('Template', t.code ? `${t.code} v${t.version} — ${t.name}` : <StatusIndicator state="error" label="not configured" />)}
        {cell('Batch', `${data.batch_number} · ${data.batch?.status || '—'}${data.batch?.completed ? ` · completed ${fmt(data.batch.completed)}` : ''}`)}
        {cell('QA review / release', (
          <>
            <div>{rv.on_file ? `review ${rv.review_status}${rv.reviewed_at ? ` ${fmt(rv.reviewed_at)}` : ''} · release ${rv.release_status}` : 'no QA review on file'}</div>
            {data.state_precondition && !data.state_precondition.in_force && <Text type="secondary" style={{ fontSize: 12 }}>{data.state_precondition.note}</Text>}
          </>
        ))}
        {cell('Seal', data.seal?.ready ? <StatusIndicator state="ok" label={`${data.seal.algorithm} · key ${String(data.seal.key_id).slice(0, 16)}…`} />
          : <StatusIndicator state="error" label={data.seal?.message || 'not ready'} />)}
        {cell('Signer', <>{sessionUser() || '—'} {data.permission?.may_certify ? '' : <StatusIndicator state="error" label="may not certify" />}</>, true)}
      </div>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: token.colorTextSecondary, marginBottom: 4 }}>Sections in this copy</div>
        <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadius, overflow: 'hidden' }}>
          {(data.coverage || []).map((c, i) => (
            <div key={c.section} style={{ padding: '6px 12px', borderTop: i ? `1px solid ${token.colorBorderSecondary}` : 'none' }}>
              <StatusIndicator state={COVERAGE_STATE[c.status] || 'unknown'} label={`${c.section} · ${c.status}`} />
              {c.reason && <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{c.reason}</Text>}
            </div>
          ))}
        </div>
      </div>
      {(data.refusals || []).map((r, i) => <Refusal key={i} refusal={r} />)}
    </div>
  );
}

/**
 * "Export certified record" on the batch screen (REX-01 R-F1).
 * The preflight is shown first; then ONE signed act — ApprovalSignModal + postAct('records/export/') —
 * with the username taken from the session. A refusal shows the server's code and message verbatim.
 */
/**
 * `bare` renders only the button (the register's row offers it as [Certify], labelled by `label`); `onDone`
 * is called after a copy is signed. The preflight and the signed act are the same either way.
 */
export default function RecordExportButton({ batchNumber, compact = false, bare = false, label = 'Export certified record', onDone }) {
  const navigate = useNavigate();
  const { canWrite, loading: accessLoading } = useCanWrite(EXPORT_MODULES);
  const [latest, setLatest] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [checking, setChecking] = useState(false);
  const [preOpen, setPreOpen] = useState(false);
  const [sign, setSign] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);

  const load = useCallback(async () => {
    if (!batchNumber || bare) return;
    try { const r = await Axios.get(`${bp}/records/exports/`, { params: { batch: batchNumber, limit: 1 }, ...auth() }); setLatest(r?.data?.data?.[0] || null); }
    catch (e) { setLatest(null); }
  }, [batchNumber, bare]);
  useEffect(() => { load(); }, [load]);

  const openPreflight = async () => {
    setChecking(true); setRefusal(null); setPreflight(null); setPreOpen(true);
    try { const r = await Axios.get(`${bp}/records/export/preflight/`, { params: { batch: batchNumber }, ...auth() }); setPreflight(r?.data?.data || null); }
    catch (e) { setRefusal({ code: e?.response?.data?.code || `HTTP ${e?.response?.status || ''}`.trim(), message: e?.response?.data?.message || 'The preflight could not be read.' }); }
    setChecking(false);
  };

  const submit = async ({ username, password, comment }) => {
    setBusy(true); setRefusal(null);
    try {
      const d = await postAct('records/export/', {
        batch_number: batchNumber, reason: comment, esign: { username, password, comment },
      });
      const x = d?.data;
      toast.success(`Certified copy #${x?.id} signed — content ${String(x?.content_hash || '').slice(0, 16)}…`);
      setSign(false); setPreOpen(false); load(); onDone?.();
    } catch (e) {
      const b = e?.response?.data || {};
      setRefusal({ code: b.code || `HTTP ${e?.response?.status || ''}`.trim(), message: b.message || 'The export was refused.', sections: b.sections });
      setSign(false);
    }
    setBusy(false);
  };

  if (!batchNumber) return null;
  const blocked = (preflight?.refusals || []).length > 0;
  const integrity = latest?.integrity;
  return (
    <Space size={8} wrap>
      <Tooltip title={accessLoading ? 'Checking access…' : canWrite ? 'Shows what the certified copy will contain, then asks for your signature.'
        : 'Signing a certified copy needs the certify permission. You can still open, verify and download the copies on file.'}>
        <Button type="primary" size={compact ? 'small' : 'middle'} icon={<FileCheck2 size={14} aria-hidden />} loading={checking}
          disabled={accessLoading} onClick={openPreflight} aria-label={`${label} — ${batchNumber}`}>{label}</Button>
      </Tooltip>
      {!bare && <Button size="small" type="link" onClick={() => navigate(`/record-exports?batch=${encodeURIComponent(batchNumber)}`)}>View certified records</Button>}
      {bare ? null : latest ? (
        <span>
          <StatusIndicator state={INTEGRITY_STATE[integrity?.integrity] || 'unknown'}
            label={integrity?.last ? `${latest.standing?.label || ''} · ${integrity.integrity === 'TAMPERED' ? 'TAMPERED' : 'verified'} ${fmt(integrity.last.at)}` : `${latest.standing?.label || ''} · ${integrity?.integrity || 'not verified'}`} />
          <Text type="secondary" style={{ fontSize: 11 }}>#{latest.id} · {latest.signed_by} · {fmt(latest.signed_at)}</Text>
        </span>
      ) : <Text type="secondary" style={{ fontSize: 11 }}>no certified copy yet</Text>}

      <PageModal open={preOpen} onCancel={() => setPreOpen(false)} destroyOnHidden width={760}
        icon={<FileCheck2 size={22} />} title="Certified copy — before you sign"
        description={`Check what the certified copy of ${batchNumber} will contain, then continue to sign.`}
        footer={(
          <div className="flex justify-end gap-2">
            <Button key="c" onClick={() => setPreOpen(false)}>Close</Button>
            <Button key="s" type="primary" disabled={checking || !preflight || blocked || !preflight?.permission?.may_certify}
              onClick={() => setSign(true)}>Continue to sign</Button>
          </div>
        )}>
        {checking ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : <Preflight data={preflight} />}
        {refusal && <div style={{ marginTop: 10 }}><Refusal refusal={refusal} /></div>}
      </PageModal>

      <ApprovalSignModal open={sign} busy={busy} usernameReadOnly requireReason
        title="Certified copy — e-signature" action="Certify" reasonLabel="Reason for this copy"
        meaning={`Meaning: ${MEANING}`}
        onCancel={() => setSign(false)} onSubmit={submit} />
    </Space>
  );
}
